import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BracketHeightRangePanel from '../components/bracket/BracketHeightRangePanel';
import BracketResultsPanel from '../components/bracket/BracketResultsPanel';
import BracketSetView from '../components/bracket/BracketSetView';
import { fetchTournaments } from '../api/competitionApplication';
import usePublicBracket from '../hooks/usePublicBracket';
import { useI18n } from '../i18n';
import { formatEventDate, normalizeCompetitionList } from '../utils/competitionList';
import {
  BRACKET_MODES,
  getBracketSetDivisions,
  getBracketSetSummaryLabel,
  isMatchFormat,
} from '../utils/bracketSet';

/** 전광판 (조회 전용, 로그인 불필요) */
function BracketDisplayPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const tournamentIdParam = searchParams.get('tournamentId');

  const [tournamentName, setTournamentName] = useState('');
  const [eventAt, setEventAt] = useState(null);
  const [location, setLocation] = useState('');
  const {
    status,
    message,
    bracketSet: bracketData,
    results,
    liveMatches,
    reload,
    selectHeightRange,
  } = usePublicBracket(tournamentIdParam);

  const divisions = useMemo(() => getBracketSetDivisions(bracketData), [bracketData]);

  useEffect(() => {
    if (!tournamentIdParam) return;
    fetchTournaments()
      .then((data) => {
        const tournament = normalizeCompetitionList(data).find(
          (item) => String(item.id) === String(tournamentIdParam)
        );
        // 이름이 없으면 화면에서 기본 제목(현재 언어)을 보여준다
        setTournamentName(tournament?.name ?? '');
        setEventAt(tournament?.eventAt ?? null);
        setLocation(tournament?.location ?? '');
      })
      .catch(() => setTournamentName(''));
  }, [tournamentIdParam]);

  const statusMessage = !tournamentIdParam
    ? t('tournament.display.noTournament')
    : message;

  const handleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div className="bracket-display-page">
      <header className="bracket-display-top">
        <p className="bracket-display-eyebrow">LIVE TOURNAMENT BOARD</p>
        <h1 className="bracket-display-title">
          {tournamentName || t('tournament.display.defaultTitle')}
        </h1>
        {(eventAt || location) && (
          <div className="bracket-display-meta">
            {eventAt && <span>📅 {formatEventDate(eventAt)}</span>}
            {location && <span>📍 {location}</span>}
          </div>
        )}
        {bracketData && (
          <p className="bracket-display-size">{getBracketSetSummaryLabel(bracketData)}</p>
        )}
        <div className="bracket-display-actions">
          <button type="button" className="btn-display-fullscreen" onClick={handleFullscreen}>
            {t('tournament.display.fullscreen')}
          </button>
        </div>
      </header>

      <div className="bracket-display-body">
        {(status === 'idle' || status === 'loading' || status === 'empty') && (
          <p className="bracket-display-status">{statusMessage}</p>
        )}
        {status === 'error' && (
          <div className="bracket-display-status bracket-display-status-error">
            <p>{statusMessage}</p>
            <button type="button" className="btn btn-retry" onClick={() => reload()}>
              {t('common.retry')}
            </button>
          </div>
        )}
        {status === 'ready' && bracketData && (
          <div className="bracket-display-board">
            {results.length > 0 && <BracketResultsPanel
                results={results}
                showMatchLog={false}
                matchList={isMatchFormat(bracketData)}
              />}
            {bracketData.mode === BRACKET_MODES.HEIGHT && (
              <BracketHeightRangePanel
                bracketSet={bracketData}
                participants={[]}
                editMode={false}
                onApplyRanges={() => {}}
                onSelectRange={selectHeightRange}
              />
            )}
            <BracketSetView bracketSet={bracketData} divisions={divisions} liveMatches={liveMatches} />
          </div>
        )}
      </div>
    </div>
  );
}

export default BracketDisplayPage;
